import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Typography,
  Link as MuiLink,
} from '@material-ui/core';
import { FormattedMessage, useIntl } from 'react-intl';
import { Input, Checkbox, FileInput } from 'components/FormInputs';
import AcceptTerms from 'components/shared/AcceptTerms';
import { validateNewUploadIDPatch } from 'logic/userForm';
import { downscaleImageFile } from 'logic/imageResize';
import { useFormik } from 'components/shared/Formik';

const uploadDocument = <FormattedMessage id="Upload document" defaultMessage="Upload document" />;

const uploadFrontSide = (
  <FormattedMessage id="Upload front side" defaultMessage="Upload front side" />
);

const uploadBackSide = <FormattedMessage id="Upload back side" defaultMessage="Upload back side" />;

// Resolves with a data URL for the preview, or null if the file can't be read.
const readPreview = (file) =>
  new Promise((resolve) => {
    try {
      const reader = new FileReader();
      reader.onload = (event) => resolve(event.target.result);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(file);
    } catch (e) {
      resolve(null);
    }
  });

const Instructions = ({ reset }) => (
  <>
    <Grid item xs={12}>
      <Typography paragraph color="textSecondary">
        <FormattedMessage
          id="userform.makeSurePhotoClear"
          defaultMessage="Make sure you photo is clear to read, with no blur or glare"
        />
      </Typography>
      <Typography paragraph color="textSecondary">
        <FormattedMessage
          id="userform.ifImageNotClear"
          defaultMessage="If the image is not clear to read, blur or glare, {clickHere} to try again"
          values={{
            clickHere: (
              <MuiLink onClick={reset}>
                <FormattedMessage id="click here" defaultMessage="click here" />
              </MuiLink>
            ),
          }}
        />
      </Typography>
    </Grid>
  </>
);

Instructions.propTypes = {
  reset: PropTypes.func,
};

const Form = ({ onSubmit, sidesCount, user, uploading }) => {
  const intl = useIntl();
  const [previews, setPreviews] = React.useState({
    front: null,
    back: null,
  });
  const [optimizing, setOptimizing] = React.useState({
    front: false,
    back: false,
  });
  const frontRef = React.useRef(null);
  const backRef = React.useRef(null);
  const mountedRef = React.useRef(true);
  // Bumped by reset(). An optimization still in flight when the form is reset
  // (document type switched, "try again" clicked) must not write its file
  // into the freshly cleared form.
  const generationRef = React.useRef(0);

  React.useEffect(
    () => () => {
      mountedRef.current = false;
    },
    [],
  );

  const formik = useFormik({
    initialValues: {
      first_name: user.first_name,
      id_last_name: user.last_name,
      id_first_name: '',
      termsOfPayment: !!user.mango_user_id,
      front: null,
      back: null,
      sidesCount,
    },
    onSubmit: async (values) => {
      return onSubmit(values);
    },
    enableReinitialize: true,
    validate: validateNewUploadIDPatch({ intl }),
    validateOnChange: false,
  });

  const {
    handleSubmit,
    handleChange,
    handleBlur,
    errors,
    values,
    isSubmitting,
    setFieldValue,
    resetForm,
  } = formik;

  // Downscale oversized phone photos before they enter the form so they fit
  // under the backend size cap; PDFs / small files pass through unchanged.
  const handleFile = async (name, file) => {
    if (!file) {
      return;
    }

    const generation = generationRef.current;
    const isCurrent = () => mountedRef.current && generationRef.current === generation;

    setOptimizing((prev) => ({ ...prev, [name]: true }));
    try {
      // The helper is contracted never to reject; fall back to the raw file
      // anyway so a picked photo is never silently dropped.
      const optimized = await downscaleImageFile(file).catch(() => file);
      // Read the preview before touching state so the button goes straight
      // from spinner to thumbnail instead of flashing the empty label.
      const preview = await readPreview(optimized);

      // Bail if the form unmounted or was reset while we were working.
      if (!isCurrent()) {
        return;
      }

      setFieldValue(name, optimized);
      setPreviews((prev) => ({ ...prev, [name]: preview }));
    } finally {
      // reset() already cleared the flags for a stale run; never write after unmount.
      if (isCurrent()) {
        setOptimizing((prev) => ({ ...prev, [name]: false }));
      }
    }
  };

  const reset = () => {
    generationRef.current += 1;
    setPreviews({ front: null, back: null });
    setOptimizing({ front: false, back: false });
    resetForm();

    if (frontRef.current) {
      frontRef.current.value = null;
    }

    if (backRef.current) {
      backRef.current.value = null;
    }
  };

  React.useEffect(() => {
    if (sidesCount !== values.sidesCount) {
      reset();
    }
  }, [sidesCount]);

  const noFiles = !values.front || (sidesCount === 2 && !values.back);

  return (
    <Box mt={4}>
      <form onSubmit={handleSubmit} noValidate>
        <Grid container spacing={2} justifyContent="center">
          <Grid item xs={12} sm={6}>
            <Box textAlign="center">
              <Button
                color="primary"
                component="label"
                htmlFor="front_side_file"
                variant={previews.front ? 'outlined' : 'contained'}
                onClick={() => frontRef.current.click()}
                size="small"
                disabled={optimizing.front}
              >
                {optimizing.front && <CircularProgress size={24} color="inherit" />}
                {!optimizing.front && !previews.front && (
                  <Typography htmlFor="front_side_file">
                    {sidesCount === 1 ? uploadDocument : uploadFrontSide}
                  </Typography>
                )}
                {!optimizing.front && previews.front && <img src={previews.front} alt="" />}
                <FileInput
                  name="front"
                  onChange={(e) => {
                    if (!e.target.files) {
                      return;
                    }

                    handleFile('front', e.target.files[0]);
                  }}
                  fileRef={(n) => {
                    frontRef.current = n;
                  }}
                />
              </Button>
            </Box>
          </Grid>
          {sidesCount > 1 && (
            <Grid item xs={12} sm={6}>
              <Box textAlign="center">
                <Button
                  color="primary"
                  component="label"
                  htmlFor="back_side_file"
                  variant={previews.back ? 'outlined' : 'contained'}
                  onClick={() => backRef.current.click()}
                  size="small"
                  disabled={optimizing.back}
                >
                  {optimizing.back && <CircularProgress size={24} color="inherit" />}
                  {!optimizing.back && !previews.back && (
                    <Typography htmlFor="back_side_file">{uploadBackSide}</Typography>
                  )}
                  {!optimizing.back && previews.back && <img src={previews.back} alt="" />}
                  <FileInput
                    value={values.back}
                    name="back"
                    onChange={(e) => {
                      if (!e.target.files) {
                        return;
                      }

                      handleFile('back', e.target.files[0]);
                    }}
                    fileRef={(n) => {
                      backRef.current = n;
                    }}
                  />
                </Button>
              </Box>
            </Grid>
          )}

          {!noFiles && <Instructions reset={reset} />}
          <Grid item xs={12}>
            <Box textAlign="left">
              <Checkbox
                name="termsOfPayment"
                value={values.termsOfPayment}
                onChange={handleChange}
                label={<AcceptTerms />}
              />
            </Box>
          </Grid>
          {!noFiles && (
            <>
              <Grid item xs={12}>
                <hr />
                <Typography variant="body1">
                  <FormattedMessage
                    id="userForm.writeAllNames"
                    defaultMessage="Write all your names as they are written on your document"
                  />
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <Input
                  name="id_first_name"
                  errors={errors}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.id_first_name}
                  label={<FormattedMessage id="userform.firstName" defaultMessage="First name" />}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Input
                  name="id_last_name"
                  errors={errors}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.id_last_name}
                  label={<FormattedMessage id="userform.lastName" defaultMessage="Last name" />}
                  required
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="body1">
                  <FormattedMessage
                    id="userForm.writeChooseNames"
                    defaultMessage="Choose what name you want to have on your public account"
                  />
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Input
                  name="first_name"
                  errors={errors}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.first_name}
                  label={<FormattedMessage id="userform.firstName" defaultMessage="First name" />}
                  required
                  disabled={!user.can_change_name}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Input
                  name="id_last_name"
                  errors={errors}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.id_last_name}
                  label={<FormattedMessage id="userform.lastName" defaultMessage="Last name" />}
                  required
                  disabled
                />
              </Grid>
            </>
          )}

          <Grid item xs={12}>
            <Box textAlign="left" mb={7}>
              <Button
                variant="contained"
                color="primary"
                type="submit"
                disabled={
                  !values.termsOfPayment ||
                  isSubmitting ||
                  noFiles ||
                  uploading ||
                  optimizing.front ||
                  optimizing.back
                }
              >
                {uploading && (
                  <FormattedMessage
                    id="userform.uploading"
                    defaultMessage="Uploading... please wait"
                  />
                )}
                {!uploading && <FormattedMessage id="Save" defaultMessage="Save" />}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </form>
    </Box>
  );
};

Form.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  sidesCount: PropTypes.number,
  user: PropTypes.shape({
    first_name: PropTypes.string,
    last_name: PropTypes.string,
    mango_user_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    can_change_name: PropTypes.bool,
  }),
  uploading: PropTypes.bool,
};

export default Form;
